import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, TrendingUp, TrendingDown, Download, Calendar, 
  ArrowUpRight, ArrowDownRight, DollarSign, Wallet, PiggyBank,
  FileText, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line, AreaChart, Area, ComposedChart
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const [profitLoss, setProfitLoss] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profit-loss');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [plRes, bsRes, cfRes] = await Promise.all([
        axios.get(`${API}/reports/profit-loss`),
        axios.get(`${API}/reports/balance-sheet`),
        axios.get(`${API}/reports/cash-flow-statement`)
      ]);
      setProfitLoss(plRes.data);
      setBalanceSheet(bsRes.data);
      setCashFlow(cfRes.data);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const expenseChartData = profitLoss?.expenses ? 
    Object.entries(profitLoss.expenses).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      amount: value
    })) : [];

  const cashFlowChartData = cashFlow?.monthly_data?.map(item => ({
    month: item.month?.substring(5) || '',
    inflow: item.inflow || 0,
    outflow: item.outflow || 0,
    net: item.net || 0
  })) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-primary">Loading reports...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white">Financial Reports</h1>
          <p className="text-muted-foreground">Comprehensive financial statements and analysis</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-orbit-border" onClick={fetchReports}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" className="border-orbit-border">
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-orbit-card border border-orbit-border">
          <TabsTrigger value="profit-loss">Profit & Loss</TabsTrigger>
          <TabsTrigger value="balance-sheet">Balance Sheet</TabsTrigger>
          <TabsTrigger value="cash-flow">Cash Flow</TabsTrigger>
        </TabsList>

        {/* Profit & Loss */}
        <TabsContent value="profit-loss" className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-4">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="bg-orbit-card border-orbit-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Revenue</p>
                      <p className="text-2xl font-heading font-bold text-emerald-400 tabular-nums">
                        {formatCurrency(profitLoss?.revenue?.total)}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-emerald-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="bg-orbit-card border-orbit-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Expenses</p>
                      <p className="text-2xl font-heading font-bold text-rose-400 tabular-nums">
                        {formatCurrency(profitLoss?.total_expenses)}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-rose-500/20 flex items-center justify-center">
                      <TrendingDown className="w-6 h-6 text-rose-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card className="bg-orbit-card border-orbit-border gradient-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Net Profit</p>
                      <p className={`text-2xl font-heading font-bold tabular-nums ${profitLoss?.net_profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {formatCurrency(profitLoss?.net_profit)}
                      </p>
                      {profitLoss?.profit_margin !== undefined && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Margin: {profitLoss.profit_margin.toFixed(1)}%
                        </p>
                      )}
                    </div>
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Expense Breakdown Chart */}
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader>
                <CardTitle className="text-white">Expense Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {expenseChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={expenseChartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis type="number" stroke="#64748b" fontSize={12} tickFormatter={(v) => `${v/1000}k`} />
                        <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={12} width={80} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#161822', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                          formatter={(value) => [formatCurrency(value), 'Amount']}
                        />
                        <Bar dataKey="amount" fill="#6366F1" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      No expense data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* P&L Details */}
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader>
                <CardTitle className="text-white">Profit & Loss Statement</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Revenue Section */}
                  <div>
                    <h4 className="text-sm font-medium text-emerald-400 uppercase tracking-wider mb-3">Revenue</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between py-2 border-b border-orbit-border">
                        <span className="text-muted-foreground">Sales</span>
                        <span className="font-mono text-white">{formatCurrency(profitLoss?.revenue?.sales)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-orbit-border">
                        <span className="text-muted-foreground">Other Income</span>
                        <span className="font-mono text-white">{formatCurrency(profitLoss?.revenue?.other_income)}</span>
                      </div>
                      <div className="flex justify-between py-2 font-semibold">
                        <span className="text-white">Total Revenue</span>
                        <span className="font-mono text-emerald-400">{formatCurrency(profitLoss?.revenue?.total)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Expenses Section */}
                  <div>
                    <h4 className="text-sm font-medium text-rose-400 uppercase tracking-wider mb-3">Expenses</h4>
                    <div className="space-y-2">
                      {profitLoss?.expenses && Object.entries(profitLoss.expenses).map(([key, value]) => (
                        <div key={key} className="flex justify-between py-2 border-b border-orbit-border">
                          <span className="text-muted-foreground capitalize">{key}</span>
                          <span className="font-mono text-white">{formatCurrency(value)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between py-2 font-semibold">
                        <span className="text-white">Total Expenses</span>
                        <span className="font-mono text-rose-400">{formatCurrency(profitLoss?.total_expenses)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Net Profit */}
                  <div className="pt-4 border-t-2 border-orbit-border">
                    <div className="flex justify-between">
                      <span className="text-lg font-semibold text-white">Net Profit</span>
                      <span className={`text-xl font-heading font-bold tabular-nums ${profitLoss?.net_profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {formatCurrency(profitLoss?.net_profit)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Balance Sheet */}
        <TabsContent value="balance-sheet" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid lg:grid-cols-3 gap-4">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="bg-orbit-card border-orbit-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Assets</p>
                      <p className="text-2xl font-heading font-bold text-emerald-400 tabular-nums">
                        {formatCurrency(balanceSheet?.assets?.total_assets)}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Wallet className="w-6 h-6 text-emerald-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="bg-orbit-card border-orbit-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Liabilities</p>
                      <p className="text-2xl font-heading font-bold text-rose-400 tabular-nums">
                        {formatCurrency(balanceSheet?.liabilities?.total_liabilities)}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-rose-500/20 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-rose-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card className="bg-orbit-card border-orbit-border gradient-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Equity</p>
                      <p className="text-2xl font-heading font-bold text-secondary tabular-nums">
                        {formatCurrency(balanceSheet?.equity?.total_equity)}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center">
                      <PiggyBank className="w-6 h-6 text-secondary" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Assets */}
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-emerald-400" />
                  Assets
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-primary uppercase tracking-wider mb-3">Current Assets</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between py-2 border-b border-orbit-border">
                        <span className="text-muted-foreground">Cash & Cash Equivalents</span>
                        <span className="font-mono text-white">{formatCurrency(balanceSheet?.assets?.current_assets?.cash)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-orbit-border">
                        <span className="text-muted-foreground">Accounts Receivable</span>
                        <span className="font-mono text-white">{formatCurrency(balanceSheet?.assets?.current_assets?.accounts_receivable)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-orbit-border">
                        <span className="text-muted-foreground">Inventory</span>
                        <span className="font-mono text-white">{formatCurrency(balanceSheet?.assets?.current_assets?.inventory)}</span>
                      </div>
                      <div className="flex justify-between py-2 font-semibold text-primary">
                        <span>Total Current Assets</span>
                        <span className="font-mono">{formatCurrency(balanceSheet?.assets?.total_current_assets)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="pt-4 border-t-2 border-orbit-border">
                    <div className="flex justify-between">
                      <span className="font-semibold text-white">Total Assets</span>
                      <span className="font-mono font-bold text-emerald-400">{formatCurrency(balanceSheet?.assets?.total_assets)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Liabilities & Equity */}
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-rose-400" />
                  Liabilities & Equity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-rose-400 uppercase tracking-wider mb-3">Liabilities</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between py-2 border-b border-orbit-border">
                        <span className="text-muted-foreground">Accounts Payable</span>
                        <span className="font-mono text-white">{formatCurrency(balanceSheet?.liabilities?.current_liabilities?.accounts_payable)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-orbit-border">
                        <span className="text-muted-foreground">Taxes Payable</span>
                        <span className="font-mono text-white">{formatCurrency(balanceSheet?.liabilities?.current_liabilities?.taxes_payable)}</span>
                      </div>
                      <div className="flex justify-between py-2 font-semibold text-rose-400">
                        <span>Total Liabilities</span>
                        <span className="font-mono">{formatCurrency(balanceSheet?.liabilities?.total_liabilities)}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-secondary uppercase tracking-wider mb-3">Equity</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between py-2 border-b border-orbit-border">
                        <span className="text-muted-foreground">Retained Earnings</span>
                        <span className="font-mono text-white">{formatCurrency(balanceSheet?.equity?.retained_earnings)}</span>
                      </div>
                      <div className="flex justify-between py-2 font-semibold text-secondary">
                        <span>Total Equity</span>
                        <span className="font-mono">{formatCurrency(balanceSheet?.equity?.total_equity)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Cash Flow Statement */}
        <TabsContent value="cash-flow" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid lg:grid-cols-4 gap-4">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="bg-orbit-card border-orbit-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Operating Activities</p>
                      <p className={`text-xl font-heading font-bold tabular-nums ${(cashFlow?.operating_activities?.net_cash_operating || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {formatCurrency(cashFlow?.operating_activities?.net_cash_operating)}
                      </p>
                    </div>
                    <ArrowUpRight className={`w-6 h-6 ${(cashFlow?.operating_activities?.net_cash_operating || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="bg-orbit-card border-orbit-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Investing Activities</p>
                      <p className={`text-xl font-heading font-bold tabular-nums ${(cashFlow?.investing_activities?.net_cash_investing || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {formatCurrency(cashFlow?.investing_activities?.net_cash_investing)}
                      </p>
                    </div>
                    <ArrowDownRight className="w-6 h-6 text-amber-400" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card className="bg-orbit-card border-orbit-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Financing Activities</p>
                      <p className="text-xl font-heading font-bold tabular-nums text-muted-foreground">
                        {formatCurrency(cashFlow?.financing_activities?.net_cash_financing)}
                      </p>
                    </div>
                    <DollarSign className="w-6 h-6 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <Card className="bg-orbit-card border-orbit-border gradient-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Net Change in Cash</p>
                      <p className={`text-xl font-heading font-bold tabular-nums ${(cashFlow?.summary?.net_change_in_cash || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {formatCurrency(cashFlow?.summary?.net_change_in_cash)}
                      </p>
                    </div>
                    <Wallet className="w-6 h-6 text-primary" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Cash Flow Chart */}
          <Card className="bg-orbit-card border-orbit-border">
            <CardHeader>
              <CardTitle className="text-white">Monthly Cash Flow Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {cashFlowChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={cashFlowChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                      <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `${v/1000}k`} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#161822', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                        formatter={(value) => [formatCurrency(value), '']}
                      />
                      <Legend />
                      <Bar dataKey="inflow" name="Cash Inflow" fill="#10B981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="outflow" name="Cash Outflow" fill="#F43F5E" radius={[4, 4, 0, 0]} />
                      <Line type="monotone" dataKey="net" name="Net Cash Flow" stroke="#6366F1" strokeWidth={2} dot={{ fill: '#6366F1' }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    <div className="text-center">
                      <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No monthly cash flow data available yet</p>
                      <p className="text-sm">Add transactions to see your cash flow trend</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Operating Activities Detail */}
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <ArrowUpRight className="w-5 h-5 text-emerald-400" />
                  Operating Activities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-orbit-border">
                    <span className="text-muted-foreground">Cash from Customers</span>
                    <span className="font-mono text-emerald-400">
                      {formatCurrency(cashFlow?.operating_activities?.cash_from_customers)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-orbit-border">
                    <span className="text-muted-foreground">Cash Paid to Suppliers</span>
                    <span className="font-mono text-rose-400">
                      {formatCurrency(cashFlow?.operating_activities?.cash_paid_suppliers)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-orbit-border">
                    <span className="text-muted-foreground">Cash Paid for Salaries</span>
                    <span className="font-mono text-rose-400">
                      {formatCurrency(cashFlow?.operating_activities?.cash_paid_salaries)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-orbit-border">
                    <span className="text-muted-foreground">Cash Paid for Utilities</span>
                    <span className="font-mono text-rose-400">
                      {formatCurrency(cashFlow?.operating_activities?.cash_paid_utilities)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-orbit-border">
                    <span className="text-muted-foreground">Other Operating Expenses</span>
                    <span className="font-mono text-rose-400">
                      {formatCurrency(cashFlow?.operating_activities?.cash_paid_other)}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 font-semibold border-t-2 border-orbit-border mt-2">
                    <span className="text-white">Net Cash from Operations</span>
                    <span className={`font-mono ${(cashFlow?.operating_activities?.net_cash_operating || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {formatCurrency(cashFlow?.operating_activities?.net_cash_operating)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-primary" />
                  Cash Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Investing Activities */}
                  <div>
                    <h4 className="text-sm font-medium text-amber-400 uppercase tracking-wider mb-3">Investing Activities</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between py-2 border-b border-orbit-border">
                        <span className="text-muted-foreground">Equipment Purchases</span>
                        <span className="font-mono text-rose-400">
                          {formatCurrency(cashFlow?.investing_activities?.equipment_purchases)}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 font-semibold text-amber-400">
                        <span>Net Cash from Investing</span>
                        <span className="font-mono">
                          {formatCurrency(cashFlow?.investing_activities?.net_cash_investing)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Financing Activities */}
                  <div>
                    <h4 className="text-sm font-medium text-blue-400 uppercase tracking-wider mb-3">Financing Activities</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between py-2 border-b border-orbit-border">
                        <span className="text-muted-foreground">Loans Received</span>
                        <span className="font-mono text-white">
                          {formatCurrency(cashFlow?.financing_activities?.loans_received)}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 font-semibold text-blue-400">
                        <span>Net Cash from Financing</span>
                        <span className="font-mono">
                          {formatCurrency(cashFlow?.financing_activities?.net_cash_financing)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Final Summary */}
                  <div className="pt-4 border-t-2 border-orbit-border space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Opening Cash Balance</span>
                      <span className="font-mono text-white">{formatCurrency(cashFlow?.summary?.opening_balance)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Net Change in Cash</span>
                      <span className={`font-mono ${(cashFlow?.summary?.net_change_in_cash || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {formatCurrency(cashFlow?.summary?.net_change_in_cash)}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-semibold pt-2 border-t border-orbit-border">
                      <span className="text-white">Closing Cash Balance</span>
                      <span className="font-mono text-primary">{formatCurrency(cashFlow?.summary?.closing_balance)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
